import { defineStore } from 'pinia';
import { ref, computed, shallowRef } from 'vue';
import {
  collection, getDocs,
  query, where, orderBy, limit, startAfter,
  type Unsubscribe, type QueryConstraint, type QuerySnapshot,
  type QueryDocumentSnapshot, type DocumentData,
} from 'firebase/firestore';
import { db } from '@/firebase/config';
// Die drei Schreibwege gehen ueber das Sperrdokument `printerKeys/{id}`
// (T-M41.8, REQ-DM-036 Praez. 4) — sie stehen an EINER Stelle (REQ-OPS-038).
import {
  legeDruckerMitSperrdokumentAn,
  aendereDruckerMitSperrdokument,
  loescheDruckerMitSperrdokument,
} from '@/services/printerKeyService';
import { liveAbo } from '@/utils/liveAbo';
// PRINTER_LIST_LIMIT ist seit dem 2026-08-17 die SEITENgroesse, nicht die
// Obergrenze der Liste (REQ-OPS-032 in der Fassung dieses Datums, T-M8.19b).
import { PRINTER_LIST_LIMIT } from '@/firebase/constants';
import {
  canLoadMoreNamed, mergeNamedPages, retainedAfterNamedLiveUpdate,
} from '@/utils/listPagination';
// Zustands- und Alarm-Vokabular kanonisch aus models.ts (REQ-DM-060).
// `ScreenMode` (REQ-DM-068 (1), T-M25.2) kommt aus derselben Quelle: die drei
// Modi sind ein Vokabular und werden NICHT ein zweites Mal aufgezaehlt.
import type {
  PrinterStatusDetail, PrinterStatusSource, PrinterStatusDetailLevel, PrinterAlarmStage,
  ScreenMode,
} from '@/firebase/models';

export type {
  PrinterStatusDetail, PrinterStatusSource, PrinterStatusDetailLevel, PrinterAlarmStage,
  ScreenMode,
};

export type PrinterType   = 'Standard' | 'U220' | 'Sunmi';
export type PrinterRuntimeStatus = 'online' | 'offline' | 'error' | 'printing' | 'ready';
/** Ausgabeart eines Kanals (REQ-DM-031 / REQ-SC-001). */
export type PrinterOutputType = 'print' | 'kitchenTv';
// `PrinterAlarmStage` (REQ-PS-023 / REQ-PS-023a) wird kanonisch aus models.ts
// bezogen und oben re-exportiert — alle fuenf Stufen sind vom Druckserver
// ausloesbar, aber nicht an jedem Modelltyp (REQ-PS-023a Praez. 3; die
// Kennzeichnung fuehrt `utils/alarmStufenWirksamkeit.ts`). *Bis zum
// 2026-09-25 „alle vier" — `paperOut` kam mit T-M42.3 hinzu (REQ-PS-023c).*

// KANONISCHE KOPPLUNG (REQ-DM-060):
// Dies ist der REICHHALTIGE, vollstaendige Printer-Typ (Single Source fuer das
// Dashboard). Der kanonische Stummel `PrinterStub` in `@/firebase/models.ts` bildet
// nur die per Spec verbindlichen Schluesselfelder (REQ-DM-031) ab und dient als
// Referenz fuer die Cloud Functions. Beide Stellen bei jeder Datenmodell-Aenderung
// an Druckern KONSISTENT mitziehen.
export interface Printer {
  id:               string;
  // ── Grunddaten ──────────────────────────────────────────────────────────
  /**
   * KANONISCHE Drucker-Kennung der Kasse (REQ-DM-035), im Payfix-Portal je
   * Drucker konfiguriert und im Alt-Umschlag als `printer_id` mitgesendet.
   *
   * ZWECK: Es ist der EINZIGE Aufloesungsschluessel des Alt-Eingangs
   * (`payfixlegacywebhook` -> `resolveLegacyPrinter`). Ein Drucker ohne dieses
   * Feld ist ueber den Alt-Eingang NICHT erreichbar — ohne Fehlermeldung an der
   * provisionierenden Stelle; der Fehler faellt erst beim echten Bon als HTTP
   * 404 an (Produktivstoerung 2026-08-10). Deshalb PFLICHT an jedem
   * Provisionierungsweg (REST-API und Verwaltungsoberflaeche, REQ-DM-036).
   *
   * ZEICHENKETTE, nie Zahl: Firestore vergleicht typstreng — ein Zahlwert waere
   * fuer die Abfrage des Alt-Eingangs still unauffindbar
   * (firebase/functions/src/index.ts:1458-1464).
   *
   * Optional deklariert, damit Bestandsdokumente vor dem Nachtrag
   * (REQ-OPS-040) und Dokumente der REST-API weiter lesbar bleiben; gelesen
   * wird ueberall `payfixPrinterId ?? number` (utils/payfixPrinterId.ts).
   */
  payfixPrinterId?: string;
  /**
   * SPIEGEL von `payfixPrinterId` mit genau EINEM Schreiber (REQ-DM-036 (1)):
   * dem einen Eingabefeld der Verwaltungsoberflaeche. Grundgeruest-Altlast, von
   * keiner Cloud Function, keinem Druckserver und keiner Rule ausgewertet und
   * ohne Eindeutigkeitsgarantie — deshalb KEINE zweite Quelle, sondern nur
   * Rueckfall der Anzeige. Rueckbau ist Aufraeum-Kandidat (Klasse OP-59).
   */
  number:           string;        // Printer ID (Spiegel von payfixPrinterId)
  name:             string;        // Bezeichnung
  /**
   * Ausgabeart (REQ-DM-031 / REQ-SC-001): `print` = physischer ESC/POS-Drucker,
   * `kitchenTv` = Bildschirm-Kanal. Strukturelle Trennung (REQ-SC-004): bei
   * `kitchenTv` gibt es KEINE Adresse/Port/Modelltyp/Bon-QR. Optional, damit
   * Bestandsdokumente ohne Feld weiter lesbar bleiben (Default `print`).
   */
  outputType?:      PrinterOutputType;
  /**
   * SHA-256-Hash (hex) des Anzeige-Schluessels des Bildschirm-Kanals
   * (REQ-SC-050, REQ-DM-060, kanonisch in models.ts als PrinterStubBase.screenKeyHash).
   * Der KLARTEXT-Anzeige-Schluessel wird – wie bei apiTokens – NIE persistiert;
   * hier liegt ausschliesslich sein Hash. `screentoken` vergleicht den
   * eingereichten Klartext gegen diesen Hash. Gesetzt/rotiert ueber die
   * Setz-/Rotier-Oberflaeche in PrinterDetailView (nur der Hash wird geschrieben).
   */
  screenKeyHash?:   string;
  /**
   * Schluesselpflicht des Bildschirm-Kanals (REQ-DM-063, REQ-SEC-024;
   * kanonisch in models.ts als PrinterStubBase.screenKeyRequired).
   *
   * VORZEICHENREGEL (i.d.F. 2026-08-24): **Schluesselpflicht NUR bei
   * ausdruecklichem `screenKeyRequired === true`.** Fehlendes Feld, `false` und
   * jeder Fremdtyp bedeuten „kein Schluessel erforderlich" — `screentoken`
   * stellt das Anzeige-Token dann ohne Schluesselpruefung aus; `screenKeyHash`
   * bleibt dabei stehen und wird NICHT geloescht (wer die Pflicht wieder
   * einschaltet, hat wieder denselben Schluessel).
   *
   * ⚠ BIS ZUM 2026-08-24 GALT DAS UMGEKEHRTE VORZEICHEN — berichtigt statt
   * geloescht (REQ-OPS-036 Praez. 6): „fehlendes Feld = Schluessel
   * erforderlich. Nur der ausdrueckliche Wert `false` lockert; `true`, ein
   * Fremdtyp und das fehlende Feld bedeuten unveraendert Schluesselzwang — so
   * behaelt jeder Bestandskanal seinen Schluessel, ohne dass ein Dokument
   * angefasst wird." Aufgehoben durch den Entscheid des Auftraggebers vom
   * 2026-08-24 (REQ-SEC-024 Praez. 3, T-M26.3/T-M26.4).
   */
  screenKeyRequired?: boolean;
  // KANONISCHER Feldname `modelType` (Vertrag mit Backend `setprinters` und
  // Daemon-Rendering print-server/src/printer.js). NICHT in `type` umbenennen.
  modelType:        PrinterType;   // Modelltyp (nur bei print): Standard | U220 | Sunmi
  ipAddress:        string;        // Adresse (nur bei print)
  port?:            number;        // Drucker-Port (nur bei print), REQ-DM-031
  /**
   * Zugeordneter Druckserver — NUR bei Ausgabeart `print` und dort seit dem
   * 2026-09-07 OPTIONAL (REQ-DM-072 (c), T-M33.7).
   *
   * ⚠ BIS ZUM 2026-09-07 STAND HIER `printserverId: string` — berichtigt statt
   * geloescht (REQ-OPS-036 Praez. 6). Der Typ war nicht bloss ungenau, er war
   * FALSCH: `POST /v1/printers` legt einen `print`-Drucker seit T-M33.2 ohne
   * dieses Feld an, und **das Feld entsteht nur mit Wert** — ein nicht
   * zugewiesener Drucker traegt es nicht, weder als `''` noch als `null`
   * (REQ-DM-072 (c)). Ein Typ, der die Abwesenheit nicht kennt, laesst den
   * naechsten Umsetzer `p.printserverId.trim()` schreiben, und das uebersetzte
   * bis heute stillschweigend.
   *
   * Bei `kitchenTv` ist das Feld VERBOTEN (REQ-SC-021). Deckungsgleich mit
   * `PrintPrinterStub.printserverId` in `firebase/models.ts` (REQ-DM-060).
   */
  printserverId?:   string;
  companyId:        string;
  // ── Drucker-Einstellungen ───────────────────────────────────────────────
  /**
   * @deprecated ALTBESTAND — vom Auftragseingang NIE gelesen (REQ-DM-073 (f)).
   * Gemessen am 2026-09-17 in `printerque-prod`: 0 von 3117 Druckern fuehren
   * eines der drei Altfelder. Der Rueckbau ist eine eigene Frage.
   */
  tischbereich?:    string;        // optional
  /**
   * @deprecated ALTBESTAND, abgeloest durch `tableRanges`. **Der
   * Auftragseingang liest dieses Feld ausdruecklich NICHT** (REQ-DM-073 (f)) —
   * es gaebe sonst zwei Quellen fuer dieselbe Tatsache. Die Verwaltungs-
   * oberflaeche liest es beim LADEN noch als Rueckfall und schreibt es nie.
   */
  vonNummer?:       number;
  /** @deprecated ALTBESTAND wie `vonNummer` — nie gelesen vom Auftragseingang (REQ-DM-073 (f)). */
  bisNummer?:       number;
  /**
   * Tischbereiche dieses Ausgabekanals (REQ-DM-073), unbegrenzt viele.
   *
   * ⚠ SEIT M43 HAT DAS FELD EINEN LESER: der Auftragseingang `ingestPrintJob`
   * (REQ-LC-048). Liegt die Tischnummer eines ARBEITSBONS ausserhalb aller
   * Bereiche, entsteht KEIN Auftrag — an jeder Ausgabeart und auch an Kitchen
   * TV, Abholscreen und Fastline (REQ-LC-048 (i), Entscheid vom 2026-09-17).
   * Ein Kassenbon wird nie gefiltert (c), ein Bon ohne lesbare Tischnummer wird
   * immer angelegt (e). *Bis zum 2026-09-17 schrieb diese Oberflaeche das Feld,
   * und niemand las es — der Produktionsbefund, der M43 ausgeloest hat.*
   *
   * AUSLEGUNG (REQ-DM-073 (c), REQ-LC-048 (f)): einschliessliche Grenzen,
   * mehrere Bereiche in ODER-Verknuepfung; ungueltige Eintraege (fehlende oder
   * nicht-numerische Grenzen, `von > bis`) werden still uebergangen. Fehlendes
   * Feld, leere Liste und eine Liste ohne gueltigen Eintrag bedeuten dasselbe:
   * kein Filter.
   *
   * Deckungsgleich mit `PrinterStubBase.tableRanges` in `firebase/models.ts`
   * (REQ-DM-060, REQ-DM-073 (g)).
   */
  tableRanges?:     Array<{ von: number; bis: number }>;
  /**
   * Bon-QR-Druck auf Papier (nur bei `print`, REQ-SC-004/030).
   * AUSSCHLUSS (REQ-SC-084 (a)): nie zusammen mit `kitchenTvEnabled === true`.
   */
  qrCode?:          boolean;
  // ── Abholscreen Einstellungen (REQ-SC-003, bei BEIDEN Ausgabearten) ──────
  abholscreenEnabled?:  boolean;   // Abholscreen-Ansicht aktiv
  abholscreenMaxItems?: number;    // Obergrenze gleichzeitig angezeigter Nummern
  abholShowName?:       boolean;   // Druckername anzeigen
  abholColorWorking?:   string;    // Farbe "in Arbeit" (hex)
  abholColorReady?:     string;    // Farbe "Abholbereit" (hex)
  /**
   * Anzeigedauer abholbereiter Bestellungen in Sekunden (REQ-SC-003).
   * GUELTIGER BEREICH 5 bis 600, Standard 60 (REQ-SC-080, T-M12.20). Die
   * Eingabe ist darauf begrenzt; ein in der Datenbank vorgefundener Wert
   * ausserhalb gilt in `completereadyorders` wie ein fehlender (Standard 60).
   */
  abholDisplaySec?:     number;
  /**
   * DIE ABSCHALTUNG des selbsttaetigen Erledigens (REQ-SC-081 Praez. 1,
   * T-M12.20). Gesetzt bleibt eine abholbereite Bestellung dieses Kanals
   * `ready`, bis sie von Hand abgeschlossen wird — `completereadyorders`
   * ueberspringt den Kanal.
   *
   * ⚠ NEGATIV GEPOLT UND ERZWUNGEN: fehlendes Feld und jeder Fremdtyp bedeuten
   * „nicht abgeschaltet" (REQ-SC-084 (c)). Positiv gepolt hiesse „Feld fehlt"
   * abgeschaltet — kein bestehender Kanal traegt es, und die abholbereiten
   * Bestellungen der ganzen Flotte blieben stehen. Gelesen wird ausschliesslich
   * ueber `readAbholAutoCompleteDisabled` (`@/utils/abholDisplayDuration`), nie
   * per blosser Wahrheitsprobe und nie mit `?? false`.
   *
   * GRENZE, die die Beschriftung nennt: die Vier-Stunden-Bereinigung
   * `completestaleorders` (REQ-SC-015) bleibt unberuehrt (OP-116, Weg (a)).
   * Der kanonische Stummel in `@/firebase/models.ts` fuehrt das Feld ebenfalls,
   * und zwar in `PrinterStubBase` — die Einstellung gilt an BEIDEN Ausgabearten
   * (REQ-DM-060, REQ-SC-003).
   */
  abholAutoCompleteDisabled?: boolean;
  // ── Kitchen TV Einstellungen ────────────────────────────────────────────
  /**
   * ZUSCHALTUNG DER KITCHEN-TV-ANSICHT (REQ-SC-084, REQ-SC-077, REQ-DM-031).
   *
   * NUR an der Ausgabeart `print` wirksam: dort schaltet dieser Wahrheitswert
   * die Kitchen-TV-Ansicht zu, waehrend der Kanal in jeder anderen Hinsicht ein
   * vollwertiger physischer Drucker bleibt (Druckserver, Adresse, Modelltyp,
   * Zustellzweig und die zeitgesteuerten Funktionen sind unveraendert).
   * An der Ausgabeart `kitchenTv` laeuft die Ansicht IMMER — das Feld ist dort
   * ohne Gegenstand, wird nicht angeboten und nicht ausgewertet (REQ-SC-084 (b));
   * der kanonische Stummel in `@/firebase/models.ts` fuehrt es deshalb
   * strukturell nur am `print`-Zweig der diskriminierten Union (REQ-DM-060).
   *
   * AUSSCHLUSS (REQ-SC-084 (a) / REQ-SC-004): nie zugleich mit `qrCode === true`.
   * AUSLEGUNG (REQ-SC-084 (c)): fehlendes Feld und jeder Fremdtyp bedeuten
   * „nicht zugeschaltet" — gelesen wird ausschliesslich ueber
   * `readKitchenTvEnabled` (`@/utils/kitchenTvSwitch`), nie per Wahrheitsprobe.
   *
   * Das Feld besteht seit dem Grundgeruest und war bis zum 2026-08-07 ein
   * Bedienelement OHNE WIRKUNG (OP-119 Punkt 1); mit T-M12.15 wird es wirksam.
   */
  kitchenTvEnabled?:    boolean;
  kitchenTvLight?:      boolean;   // Kitchen TV Light-Modus (REQ-SC-074)
  /**
   * DER AKTIVE BILDSCHIRM-MODUS DIESES KANALS (REQ-DM-068, REQ-SC-104 (1)).
   *
   * Genau EIN Modus ist an einem Kanal aktiv; dieses Feld nennt ihn. Geschrieben
   * wird es AUSSCHLIESSLICH serverseitig ueber die Cloud Function `kitchenaction`
   * (Aktion `setScreenMode`, REQ-SC-063, REQ-DM-068 (3)) — weder das Portal noch
   * eine Bildschirm-Ansicht schreibt es ueber das Web-SDK, und die
   * Firestore-Rules bleiben dafuer unveraendert (ein Anzeige-Token darf
   * `printers` nicht schreiben).
   *
   * ⚠ FEHLENDES FELD UND JEDER FREMDWERT BEDEUTEN „KEIN MODUS AKTIV"
   * (REQ-DM-068 (2)) — nicht `normal`. Es gibt KEINEN Vorgabewert, KEINE
   * Datenwanderung und ausdruecklich KEINE Ableitung aus `kitchenTvLight` oder
   * `kitchenTvFastLine`: die Altfelder sagen, was jemand einmal im Portal
   * eingestellt hat, dieses Feld sagt, welcher Bildschirm den Kanal gerade
   * uebernommen hat (REQ-SC-104 (5)). Die Altfelder bleiben am Dokument geduldet
   * und werden nicht geraeumt (REQ-SC-103 (4)).
   *
   * ⚠ KEIN INDEX (REQ-DM-068 (4)): das Feld wird gelesen, nie abgefragt — es
   * steht in keiner `where`- und in keiner `orderBy`-Bedingung. Wer je darauf
   * filtert, braucht zuerst einen Index (REQ-DM-050, REQ-OPS-039).
   *
   * Es steuert die ANSICHTSLOGIK und sonst nichts (REQ-DM-068 (6)); die
   * Ausgabeart bleibt der alleinige Traeger der Zustellung (REQ-SC-020/021/022).
   * BEIDE Printer-Typen fuehren das Feld mit demselben Wortlaut (REQ-DM-060,
   * REQ-DM-068 (7)): `stores/printerStore.ts` und `firebase/models.ts`.
   */
  activeScreenMode?:    ScreenMode;
  /**
   * Der Artikel-Countdown startet selbsttaetig (REQ-SC-077, REQ-SC-071).
   *
   * FESTER NAME `autoStartTimer` (Auftraggeber-Entscheid 2026-08-07 zu OP-119,
   * Weg (a)) — bis zum 2026-08-11 fuehrte das Repo hier einen anderen Namen.
   * Er steht bewusst NICHT hier: der Waechter
   * `test/kanalEinstellungFeldnamen.test.ts` verbietet ihn woertlich, samt
   * Kommentaren; nachzulesen ist er in OP-119. Die Umbenennung war KEINE
   * Datenwanderung — der Altname wurde von keiner Ansicht und keiner Cloud
   * Function ausgewertet, nur geschrieben. Der kanonische Stummel in
   * `@/firebase/models.ts` fuehrt das Feld seit T-M12.18 ebenfalls, und zwar in
   * `PrinterStubBase` — die Einstellung gilt an der Ausgabeart `kitchenTv`
   * ebenso wie an einem `print`-Kanal mit zugeschalteter Ansicht (REQ-DM-060).
   */
  autoStartTimer?:      boolean;
  /**
   * Fast Line — ein MODUS DES ABHOLSCREENS, kein Modus des Kitchen TV
   * (REQ-SC-085). Ist der Wert gesetzt, laeuft der Abholscreen dieses Kanals im
   * Fastline-Modus.
   *
   * FESTER NAME `kitchenTvFastLine` (OP-119, Weg (a)); bis zum 2026-08-11 fuehrte
   * das Repo hier den blossen Modusnamen als Feldnamen — verboten als
   * OBJEKTSCHLUESSEL, nicht als Wort (die Route `/fastline/:printerId`, die Datei
   * `FastlineView.vue` und der i18n-Namensraum bleiben, REQ-SC-085).
   * Das Praefix ist eine HISTORISCHE NAMENSFORM und
   * behauptet keine Zugehoerigkeit zum Kitchen TV (REQ-SC-085 (c)) — wer aus dem
   * Namen auf den Ort der Einstellung schliesst, schliesst falsch: sie wird in
   * der Gruppe der ABHOLSCREEN-Einstellungen angeboten, an jedem Kanal mit
   * zugeschaltetem Abholscreen (REQ-SC-085 (b), Traeger T-M12.3).
   */
  kitchenTvFastLine?:   boolean;
  /**
   * DER DRUCK IST ABBESTELLT (REQ-SC-077, REQ-LC-043, T-M15.7).
   *
   * Ist der Wert gesetzt und die Kitchen-TV-Ansicht zugeschaltet, wird der
   * ARBEITSBON an diesem Kanal nicht mehr gedruckt: `ingestPrintJob` legt ihn
   * unmittelbar mit `status: 'completed'`, `completedAt` und
   * `deliveredWithoutPrint: true` an — ohne `printedAt`. Der KASSENBON wird
   * weiterhin gedruckt (REQ-LC-009 Abgrenzung 1).
   *
   * Das Feld war bis zum 2026-08-07 ein Bedienelement OHNE Wirkung und stand
   * deshalb in keiner Quelldatei; mit T-M15.7 wird es wirksam und erstmals
   * angeboten (REQ-PS-023a: kein Bedienelement ohne Wirkung — und umgekehrt).
   * Der kanonische Stummel in `@/firebase/models.ts` fuehrt es ebenfalls, und
   * zwar in `PrinterStubBase` (REQ-DM-060): REQ-SC-077 fuehrt die Einstellung
   * an BEIDEN Ausgabearten. Gelesen wird `=== true` — fehlendes Feld und jeder
   * Fremdtyp bedeuten „nicht gesetzt".
   */
  disablePrintingWhenKitchenTv?: boolean;
  // ── ZWEI FELDER SIND HIER MIT T-M12.38 ENTFALLEN (2026-08-10) ────────────
  // Der Bon-Druckmodus und der Zieldrucker des Schlussbons standen an dieser
  // Stelle ohne jede Wirkung (REQ-PS-023a); der zweite hatte ueberdies nie
  // einen Schreibweg. Payfix woertlich auf die Rueckfrage, ob der Bon-Druckmodus
  // eine Anzeige- oder eine Druck-Einstellung war: „weder (A) noch (B) —
  // Einstellung entfernen/verwerfen" (`payfix-antwort-bonprintmode-2026-08-10.md`,
  // OP-120 geschlossen). KEINE Datenwanderung — keine Ansicht hat sie gelesen.
  // Die Feldnamen stehen bewusst NICHT hier: der Waechter
  // `test/entfalleneDruckerfelder.test.ts` verbietet sie in `dashboard/src`
  // woertlich, samt Kommentaren. Nachzulesen sind sie dort und in OP-120.
  // ── Alarm-Einstellungen (REQ-PS-024, je Drucker) ─────────────────────────
  alarmEnabled?:    boolean;               // Alarmierung aktiv
  alarmMuted?:      boolean;               // vollstaendig stummgeschaltet
  alarmStages?:     PrinterAlarmStage[];   // alarmierte Stufen (offline, paperLow …)
  // ── Archivierung (REQ-SEC-005) ───────────────────────────────────────────
  /**
   * Archiviert = deaktiviert. NUR der ausdrueckliche Wert `true` gilt als
   * archiviert; fehlendes Feld und `false` sind aktiv. Der Webhook weist
   * Auftraege an archivierte Drucker ab (T-M7.7); die Oberflaeche bietet sie
   * darum nicht mehr als Zielkanal an (utils/printerSelection.ts).
   */
  archived?:        boolean;
  archivedAt?:      unknown;
  // ── Runtime (vom Pi geschrieben) ────────────────────────────────────────
  status?:          PrinterRuntimeStatus;   // 'ready' bei Erfolg, 'error' bei Fehler (REQ-LC-042/REQ-PS-020)
  /**
   * Feinere, MODELLABHAENGIGE Detailtiefe des Zustands (REQ-PS-020a), additiv
   * zum groben `status` — eine LISTE technisch-englischer Kennungen, vom
   * Druckserver aus `deriveState` geschrieben (print-server/src/printer-status.js).
   * `status` bleibt dabei unveraendert bei `ready` | `error` | `offline` (OP-15);
   * `paperLow` allein ist KEIN Fehler und laesst `status` auf `ready`.
   * Uebersetzung ausschliesslich im UI (utils/printerStatus.ts + vue-i18n).
   */
  statusDetail?:      PrinterStatusDetail[];
  /** Welche der drei Quellen den Zustand gesetzt hat (REQ-PS-010/011/012). */
  statusSource?:      PrinterStatusSource;
  /** Detailtiefe, die das Modell ueberhaupt liefern kann (REQ-PS-001). */
  statusDetailLevel?: PrinterStatusDetailLevel;
  /** Zeitpunkt der letzten Zustandsschreibung durch den Druckserver. */
  statusUpdatedAt?:   unknown;
  lastError?:       string;                 // letzte Fehlermeldung (nur bei status 'error')
  // ⚠ ENTFALLEN AM 2026-09-18 (T-M41.10, REQ-OPS-034 Praez. 5 (c); berichtigt
  // statt stillschweigend geloescht, REQ-OPS-036 Praez. 6). HIER STAND DAS
  // ZAEHLFELD DER SPALTE „Offene Bons". Es wurde an genau EINER Stelle
  // geschrieben — bei der Anlage eines Druckers, auf `0` — und danach von
  // niemandem fortgeschrieben: die Spalte behauptete im Stoerfall „0", waehrend
  // sich eine Warteschlange staute (OP-78, REQ-PS-021). Die Spalte zaehlt
  // seither serverseitig ueber `countPendingForPrinters` und kennt die vier
  // Auspraegungen aus REQ-OPS-034 Praez. 1.
  // Der Feldname steht bewusst NICHT mehr im Produktivcode — dieselbe Form wie
  // bei den fuenf wirkungslosen Kanal-Einstellungen (T-M12.38); seine
  // Geschichte und der Waechter dagegen liegen in
  // `test/offeneBonsZaehlung.test.ts`. Bestandsdokumente behalten das Feld
  // (keine Bereinigung, REQ-OPS-040 / REQ-OPS-034 Praez. 5 (d)).
  createdAt?:       unknown;
}

/**
 * Gemeinsame Abfrageform der Druckerliste — EINE Stelle fuer die Live-Seite und
 * fuer das Nachladen (REQ-DM-050/050a, T-M8.19b).
 *
 * Form: `companyId` als Gleichheitsfilter (nur im mandantengebundenen Zweig,
 * REQ-OPS-032 Praez. 1), danach die Sortierung nach `name` aufsteigend; Firestore
 * ergaenzt implizit `__name__` als Tiebreak, die Ordnung ist damit total und
 * ueber Seitengrenzen hinweg stabil.
 *
 * Feldwahl `name` statt `number` (unveraendert aus T-M8.19a): die REST-API
 * `POST /v1/printers` schreibt kein `number`-Feld, eine Sortierung danach haette
 * REST-provisionierte Drucker unsichtbar gemacht.
 *
 * DASS BEIDE ABFRAGEN HIER HERKOMMEN, IST DER KERN: laufen Live-Seite und
 * Cursor-Seite auseinander, verschiebt sich die Seitengrenze gegen den Cursor —
 * und genau dort entstehen Doppel- und Fehlanzeigen. Cursor-Pagination aendert
 * die Abfrageform NICHT, sondern nur ihren Startpunkt; es braucht deshalb
 * KEINEN neuen Index (belegt in `test/firestoreIndexes.test.ts`).
 */
function printersQueryConstraints(companyId?: string): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];
  if (companyId) constraints.push(where('companyId', '==', companyId));
  constraints.push(orderBy('name'));
  return constraints;
}

/** Undurchsichtiger Cursor einer Seite: das LETZTE Dokument der Seite. */
type PrinterPageCursor = QueryDocumentSnapshot<DocumentData>;

export const usePrinterStore = defineStore('printer', () => {
  // ── Seiten (REQ-OPS-032 in der Fassung vom 2026-08-17, T-M8.19b) ──────────
  /** Erste Seite — laufend aktualisiert (onSnapshot). */
  const livePage    = ref<Printer[]>([]);
  /** Nachgeladene Seiten — statisch, in Ladereihenfolge. */
  const staticPages = ref<Printer[][]>([]);
  /** Unter die Live-Seite abgesunkene Zeilen (sonst entstuende dort eine Luecke). */
  const retained    = ref<Printer[]>([]);

  /**
   * Die angezeigte Druckerliste: Live-Seite, nachgeladene Seiten und
   * abgesunkene Zeilen in EINER, nach der Abfragereihenfolge sortierten Liste
   * ohne doppelte Kennungen. Frueher war das eine schlichte `ref` mit der
   * einen, bei 100 gekappten Momentaufnahme.
   */
  const printers  = computed<Printer[]>(
    () => mergeNamedPages(livePage.value, staticPages.value, retained.value),
  );

  const isLoading = ref(false);
  const error     = ref<string | null>(null);

  /** Rohe Dokumentzahl der Live-Seite bzw. der zuletzt nachgeladenen Seite. */
  const livePageSize = ref(0);
  const lastPageSize = ref<number | null>(null);

  const isLoadingMore = ref(false);
  /** Fehler des Nachladens — getrennt, damit er die geladene Liste nicht ersetzt. */
  const loadMoreError = ref<string | null>(null);

  /** Cursor der Live-Seite; nur gueltig, solange nichts nachgeladen wurde. */
  const _liveCursor   = shallowRef<PrinterPageCursor | null>(null);
  /** Cursor der zuletzt nachgeladenen Seite — eingefroren, sobald gesetzt. */
  const _staticCursor = shallowRef<PrinterPageCursor | null>(null);
  /** Mandant der laufenden Abfrage — das Nachladen braucht denselben Filter. */
  let   _companyId: string | undefined;

  /** Cursor, an dem die naechste Seite ansetzt. */
  function nextCursor(): PrinterPageCursor | null {
    return staticPages.value.length ? _staticCursor.value : _liveCursor.value;
  }

  /**
   * Sind weitere Drucker nachladbar (REQ-OPS-032)?
   *
   * ⚠ DASSELBE MERKMAL HIESS BIS ZUM 2026-08-17 `limitReached` UND BEDEUTETE
   * DAS GEGENTEIL EINER HANDLUNG: „die Liste ist hier zu Ende, mehr ist nicht
   * zu sehen" (T-M8.19a). Es ist BERICHTIGT, nicht ersetzt: der Name
   * `limitReached` bleibt unten als Merkmal bestehen und zeigt jetzt auf
   * `hasMore` — dieselbe Beobachtung („eine volle Seite kann mehr verbergen"),
   * andere Folge (nachladen statt kapitulieren). Wer ihn liest, bekommt
   * weiterhin die richtige Auskunft: die angezeigte Liste ist nicht
   * vollstaendig.
   */
  const hasMore = computed(
    () => canLoadMoreNamed(livePageSize.value, lastPageSize.value, PRINTER_LIST_LIMIT)
       && !!nextCursor(),
  );

  /**
   * Ist die angezeigte Liste moeglicherweise unvollstaendig (REQ-OPS-032)?
   *
   * Beibehaltener Name aus T-M8.19a — er wird an mehreren Stellen als „die
   * Auskunft dieser Liste ist eine Untergrenze" gelesen
   * (`utils/printerName.ts`, `utils/printserverPrinterCount.ts`), und diese
   * Lesart bleibt richtig. Neu ist allein, dass daneben ein Weg steht.
   */
  const limitReached = computed(() => hasMore.value);

  /** Zahl der geladenen Seiten (1 = nur die Live-Seite). */
  const loadedPages = computed(() => 1 + staticPages.value.length);

  /**
   * Steht ein Teil der Liste still? Ab der ersten nachgeladenen Seite gilt die
   * Live-Aktualisierung nur noch fuer die erste Seite — das ist in der
   * Oberflaeche zu sagen, statt es zu verschweigen (Akzeptanzkriterium 4).
   */
  const hasStaticPages = computed(() => staticPages.value.length > 0);
  /**
   * HAELT DER STORE GERADE EINEN LISTENER — und auf welchen Mandanten?
   * (T-M17.6, REQ-PS-021)
   *
   * Der Zustand ist nach aussen sichtbar, weil MEHRERE Ansichten denselben
   * Store fuellen und `PrintersView` ihn beim Verlassen wieder abraeumt. Eine
   * Ansicht, die die Druckerliste BRAUCHT (die Auftragsliste braucht sie fuer
   * Namen und Filter), kann daraus ableiten, ob sie selbst abonnieren muss —
   * statt sich darauf zu verlassen, welche Seite vorher offen war. Gelesen
   * wird das ueber die reine Regel `utils/printerSubscription.ts`.
   */
  const isSubscribed        = ref(false);
  const subscribedCompanyId = ref<string | null>(null);
  let   unsub: Unsubscribe | null = null;

  /** Seitenzustand zuruecksetzen (Mandantenwechsel, Abmeldung). */
  function resetPages(): void {
    livePage.value      = [];
    staticPages.value   = [];
    retained.value      = [];
    livePageSize.value  = 0;
    lastPageSize.value  = null;
    _liveCursor.value   = null;
    _staticCursor.value = null;
    loadMoreError.value = null;
    isLoadingMore.value = false;
  }

  /**
   * Live-Abo der ERSTEN Seite (REQ-OPS-032, REQ-PS-021).
   *
   * Live ist bewusst NUR die erste Seite — dieselbe benannte Abweichung wie in
   * der Auftragsliste (REQ-OPS-033, T-M9.3): ein `onSnapshot` ueber alle
   * geladenen Seiten muesste bei jeder Aenderung die Seitengrenzen neu
   * bestimmen, und genau dort entstehen Doppel- und Fehlanzeigen. Nachgeladene
   * Seiten sind Cursor-Abfragen und stehen still; die Oberflaeche sagt das
   * (`hasStaticPages`).
   */
  function subscribe(companyId?: string) {
    // Always tear down the old listener before creating a new one
    // (fixes bug: old guard 'if (unsub) return' prevented company-switch)
    unsub?.();
    unsub = null;
    isLoading.value = true;
    error.value     = null;
    // Beim Mandantenwechsel nicht nachhallen: weder die Seiten des vorigen
    // Mandanten noch das Merkmal „es gibt mehr". Beides gilt erst wieder, wenn
    // die naechste Momentaufnahme es belegt.
    resetPages();
    _companyId = companyId;
    const col = collection(db, 'printers');
    // Deterministische Auswahl an der SEITENgrenze (T-M8.19a/b, REQ-OPS-031 /
    // REQ-RP-034): eine Seite OHNE `orderBy` lieferte eine undefinierte
    // Teilmenge (Dokument-ID-Reihenfolge) — und ein Cursor darauf waere
    // bedeutungslos. Die Abfrageform kommt aus EINER Stelle, siehe
    // `printersQueryConstraints`.
    const q = query(col, ...printersQueryConstraints(companyId), limit(PRINTER_LIST_LIMIT));
    isSubscribed.value        = true;
    subscribedCompanyId.value = companyId ?? null;
    // Fehlerweg, Protokoll und Rueckzugsstaffel kommen aus der EINEN
    // Bildungsstelle (REQ-OPS-053 (e), T-M21.4).
    unsub = liveAbo<QuerySnapshot>({
      collection: 'printers',
      companyId,
      ziel: () => q,
      onData: snap => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as Printer));
        // Vor dem Ueberschreiben festhalten, was unter die Live-Seite absinkt —
        // sonst risse an der Seitengrenze eine Luecke auf.
        retained.value     = retainedAfterNamedLiveUpdate(
          printers.value, docs, staticPages.value, PRINTER_LIST_LIMIT,
        );
        livePage.value     = docs;
        livePageSize.value = snap.docs.length;
        // Solange nichts nachgeladen wurde, wandert der Cursor mit der
        // Live-Seite mit; danach ist er eingefroren.
        if (staticPages.value.length === 0) {
          _liveCursor.value = snap.docs.length
            ? (snap.docs[snap.docs.length - 1] as PrinterPageCursor)
            : null;
        }
        isLoading.value = false;
      },
      // Der Zustand bleibt WAHR: das „abonniert"-Merkmal wird zurueckgenommen
      // (OP-151) — sonst behauptete der Store weiter, jemand hoere zu, und
      // `utils/printerSubscription.ts` liesse jede Ansicht darauf vertrauen.
      onFehler: err => {
        console.error('[PrinterStore] Firestore error:', err);
        error.value               = err.message;
        isLoading.value           = false;
        isSubscribed.value        = false;
        subscribedCompanyId.value = null;
      },
      // Der Rueckweg: die Staffel hat das Abo zurueckgeholt, also faellt auch
      // der Fehlzustand — sonst meldete der Rahmen-Hinweis (T-M21.5) „nicht
      // mehr live" ueber Daten, die gerade wieder eintreffen.
      onErholt: () => {
        error.value               = null;
        isSubscribed.value        = true;
        subscribedCompanyId.value = companyId ?? null;
      },
      onStill: () => {
        isLoading.value           = false;
        isSubscribed.value        = false;
        subscribedCompanyId.value = null;
      },
    });
  }

  /**
   * Die naechste Seite nachladen (REQ-OPS-032 in der Fassung vom 2026-08-17).
   *
   * Bewusst `getDocs` und nicht `onSnapshot`: nachgeladene Seiten stehen still.
   * Bewusst Cursor (`startAfter`) und nicht ein wachsendes `limit`: so bleibt
   * die Zahl der je Abfrage gelesenen Dokumente beschraenkt, statt mit jeder
   * Seite die ganze Liste erneut zu lesen. Fehler werden gemeldet, nicht
   * verschluckt — sonst saehe die Ansicht wie das Ende der Liste aus.
   */
  async function loadMore(): Promise<void> {
    const cursor = nextCursor();
    if (!cursor || isLoadingMore.value || !hasMore.value) return;

    isLoadingMore.value = true;
    loadMoreError.value = null;
    try {
      const snap = await getDocs(query(
        collection(db, 'printers'),
        ...printersQueryConstraints(_companyId),
        startAfter(cursor),
        limit(PRINTER_LIST_LIMIT),
      ));
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() } as Printer));
      staticPages.value  = [...staticPages.value, docs];
      lastPageSize.value = snap.docs.length;
      if (snap.docs.length) {
        _staticCursor.value = snap.docs[snap.docs.length - 1] as PrinterPageCursor;
      }
    } catch (err: unknown) {
      loadMoreError.value = (err as { message?: string }).message ?? 'Unbekannter Fehler';
    } finally {
      isLoadingMore.value = false;
    }
  }

  function unsubscribe() {
    unsub?.();
    unsub = null;
    // Die Liste wird NICHT geleert (unveraendertes Verhalten): eine Ansicht,
    // die den Bestand noch anzeigt, soll ihn nicht mitten im Verlassen
    // verlieren. Sichtbar wird ab jetzt aber, dass niemand mehr zuhoert —
    // sonst ist ein abgeraeumtes Abo von einem lebenden nicht zu unterscheiden.
    isSubscribed.value        = false;
    subscribedCompanyId.value = null;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // DIE DREI SCHREIBWEGE GEHEN SEIT DEM 2026-09-18 UEBER DAS SPERRDOKUMENT
  // (T-M41.8, Dashboard-Teil; REQ-DM-036 Praez. 4, OP-125)
  //
  // ⚠ BERICHTIGT STATT GELOESCHT (REQ-OPS-036 Praez. 6): bis zum 2026-09-18
  // standen hier drei Einzeiler — `addDoc`, `updateDoc`, `deleteDoc` auf
  // `printers`. Sie waren nicht falsch, sie waren UNGESCHUETZT: die
  // Eindeutigkeit von `payfixPrinterId` haing allein an der Vorab-Auskunft
  // (`checkpayfixprinterid`, REQ-SEC-034), und zwischen Auskunft und
  // Schreibvorgang liegt ein Fenster. **Die Auskunft BLEIBT** — sie nennt die
  // Firma (REQ-DM-036 Praez. 1) und ist der Regelweg; das Sperrdokument ist die
  // GARANTIE dahinter.
  //
  // Die Transaktionen selbst stehen an EINER Stelle und nicht hier verstreut:
  // `services/printerKeyService.ts` (REQ-OPS-038). Der Store bleibt die
  // Bedienstelle der Ansichten — die Aufrufer haben sich nicht geaendert.
  // ───────────────────────────────────────────────────────────────────────────

  async function addPrinter(data: Omit<Printer, 'id'>) {
    await legeDruckerMitSperrdokumentAn(data as DocumentData);
  }

  async function updatePrinter(id: string, data: Partial<Omit<Printer, 'id'>>) {
    await aendereDruckerMitSperrdokument(id, data as DocumentData);
  }

  /**
   * ⚠ DAS IST DER EINZIGE ECHTE LOESCHWEG DES SYSTEMS (REQ-DM-036 Praez. 4
   * Nachf. (ii), Entscheid des Auftraggebers vom 2026-09-18). Gerufen wird er
   * aus `views/PrintersView.vue`. **`DELETE /v1/printers/:id` ist es NICHT** —
   * das ist eine weiche Archivierung (REQ-SEC-005), sie laesst das
   * Sperrdokument stehen, und der archivierte Drucker bleibt ueber den
   * Alt-Eingang aufloesbar.
   */
  async function deletePrinter(id: string) {
    await loescheDruckerMitSperrdokument(id);
  }

  /*
   * ⚠ HIER STAND BIS ZUM 2026-09-01 `findByPayfixPrinterId` — die SYSTEMWEITE
   * Suche nach einer Payfix-Drucker-Kennung (Gleichheit auf das Feld, ohne
   * Mandantenfilter, mit einer Obergrenze von fuenf Treffern). Berichtigt statt
   * geloescht (REQ-OPS-036 Praez. 6); ihre Begruendung lautete:
   *
   *   „Sucht Drucker mit einer bestimmten Payfix-Drucker-Kennung
   *   (REQ-DM-036 (3)). SYSTEMWEIT, also ausdruecklich OHNE Mandantenfilter:
   *   `payfixPrinterId` ist per REQ-DM-035 systemweit eindeutig, weil der
   *   Alt-Eingang die Kennung ohne Mandantenkenntnis aufloest. Eine
   *   mandantenweise Pruefung liesse genau die Dublette zu, die den Bon still am
   *   fremden Standort ausgeben wuerde."
   *
   * **SIE IST MIT T-M30.11 ENTFALLEN, UND ZWAR NICHT, WEIL SIE FALSCH WAR —
   * sondern weil sie nach dem Vollzug der Mandantentrennung nicht mehr
   * DURCHKOMMT.** Eine Abfrage ohne `companyId`-Filter wird fuer die Rolle
   * `user` von den Rules als GANZES abgewiesen und nicht etwa gekuerzt
   * (REQ-SEC-029 (e)); ein Kundenbenutzer verloere damit nicht die Auskunft,
   * sondern die PRUEFUNG (REQ-SEC-034 (a)).
   *
   * **DER ERSATZ IST GEBAUT UND WIRD GERUFEN:** die Cloud Function
   * `checkpayfixprinterid` (REQ-SEC-034, T-M30.10) ueber
   * `firebase/payfixPrinterIdApi.ts`. Die Abfrageform selbst hat sich dabei
   * NICHT geaendert — sie ist umgezogen (REQ-SEC-034 (d): kein zweiter
   * Aufloesungsweg, kein neuer Index).
   *
   * ⚠ **DAMIT IST AUCH DIE BENANNTE AUSNAHME DES MANDANTEN-WAECHTERS
   * GEGENSTANDSLOS** (`test/anspruchUndMandantenwahl.test.ts`, Vermerk 1 am
   * Ausgangstor T-M30.7) und die Registrierung der Abfrageform im
   * Index-Waechter ebenso (`test/firestoreIndexes.test.ts`). Beide sind mit
   * ihrem Gegenstand gefallen: eine Registrierung ohne Gegenstand ist eine
   * Falschaussage im Waechter.
   */

  return {
    printers, isLoading, error, limitReached, isSubscribed, subscribedCompanyId,
    hasMore, isLoadingMore, loadMoreError, loadedPages, hasStaticPages,
    subscribe, loadMore, unsubscribe, addPrinter, updatePrinter, deletePrinter,
  };
});
